/**
 * Document Streaming Hook
 * 
 * Handles real-time streaming of AI-generated document operations into TipTap editor.
 * Uses server-side operations with section_update events for atomic updates.
 * 
 * Architecture:
 * - Backend applies operations to JSON document and saves to S3
 * - Backend streams section_update events with complete section data
 * - Frontend updates TipTap view to match server's document
 * - No client-side operation logic needed (removed ~1300 lines of legacy code)
 */

import { useEffect, useRef, useCallback } from 'react';
import { logger } from '../utils/logger';
import { Editor } from '@tiptap/react';
import type { DocumentOperation } from '../types/document-operations';
import type { DocumentSection } from '../types/document-schema';
import { sectionToTipTapNodes } from '../utils/document-to-tiptap';

const HIGHLIGHT_DURATION = 10000; // 10 seconds - long enough to see what changed

/**
 * Scroll to a section by its ID attribute in the DOM.
 * More reliable than position-based scrolling after content changes.
 * Uses requestAnimationFrame to ensure DOM has updated.
 */
function scrollToSectionById(sectionId: string): void {
  // Wait for DOM to update after TipTap content changes
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      try {
        // Find the heading element by its ID attribute
        const element = document.querySelector(`[id="${sectionId}"]`);
        
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
          logger.debug('[DocumentStreaming] Scrolled to section:', sectionId);
        } else {
          logger.debug('[DocumentStreaming] Scroll skipped: section not found in DOM:', sectionId);
        }
      } catch (error) {
        logger.debug('[DocumentStreaming] Scroll skipped due to error:', error);
      }
    });
  });
}

const highlightTimers: Set<NodeJS.Timeout> = new Set();

/**
 * Schedule highlight removal for specific blocks after HIGHLIGHT_DURATION.
 * More targeted than section-level removal.
 */
function scheduleHighlightRemovalForBlocks(
  editor: Editor,
  blockIds: string[]
): void {
  const timer = setTimeout(() => {
    try {
      for (const blockId of blockIds) {
        // Find the block by its data-block-id attribute
        let blockStart: number | null = null;
        let blockEnd: number | null = null;
        
        editor.state.doc.descendants((node, pos) => {
          if (node.attrs?.['data-block-id'] === blockId || node.attrs?.id === blockId) {
            blockStart = pos;
            blockEnd = pos + node.nodeSize;
            return false;
          }
        });
        
        if (blockStart !== null && blockEnd !== null && blockEnd > blockStart) {
          const docSize = editor.state.doc.content.size;
          const from = Math.min(blockStart, docSize - 1);
          const to = Math.min(blockEnd, docSize);
          
          if (from < to && from >= 0) {
            editor.chain()
              .setTextSelection({ from, to })
              .unsetMark('diffHighlight')
              .run();
          }
        }
      }
      logger.debug('[DocumentStreaming] Removed highlights from blocks:', blockIds.join(', '));
    } catch (error) {
      logger.debug('[DocumentStreaming] Highlight removal skipped due to error:', error);
    }
    
    highlightTimers.delete(timer);
  }, HIGHLIGHT_DURATION);
  
  highlightTimers.add(timer);
}

/**
 * Schedule highlight removal for an entire section after HIGHLIGHT_DURATION.
 * Finds the section by its heading ID and removes highlights from all content within.
 */
function scheduleHighlightRemovalForSection(
  editor: Editor, 
  sectionId: string
): void {
  const timer = setTimeout(() => {
    try {
      // Find section boundaries by heading ID
      let sectionStart: number | null = null;
      let sectionEnd: number | null = null;
      let sectionLevel: number | null = null;
      
      // First pass: find the section heading
      editor.state.doc.descendants((node, pos) => {
        if (node.type.name === 'heading' && node.attrs.id === sectionId) {
          sectionStart = pos;
          sectionLevel = node.attrs.level || 2;
          return false;
        }
      });
      
      if (sectionStart === null || sectionLevel === null) {
        logger.debug('[DocumentStreaming] Highlight removal skipped: section not found:', sectionId);
        highlightTimers.delete(timer);
        return;
      }
      
      // Second pass: find section end - stop at ANY heading (preserves subsections)
      sectionEnd = editor.state.doc.content.size;
      let foundEnd = false;
      
      editor.state.doc.descendants((node, pos) => {
        if (foundEnd) return false;
        if (pos <= sectionStart!) return;
        
        if (node.type.name === 'heading') {
          // Stop at ANY heading - this only affects the section's direct content
          sectionEnd = pos;
          foundEnd = true;
          return false;
        }
      });
      
      // Remove highlights from entire section range
      if (sectionStart !== null && sectionEnd !== null && sectionEnd > sectionStart + 1) {
        // Validate positions are within document bounds
        const docSize = editor.state.doc.content.size;
        const from = Math.min(sectionStart + 1, docSize - 1);
        const to = Math.min(sectionEnd - 1, docSize - 1);
        
        if (from < to && from > 0) {
          editor.chain()
            .setTextSelection({ from, to })
            .unsetMark('diffHighlight')
            .setTextSelection(Math.min(sectionStart, docSize - 1)) // Reset cursor position
            .run();
          logger.debug('[DocumentStreaming] Removed highlights from section:', sectionId);
        } else {
          logger.debug('[DocumentStreaming] Highlight removal skipped: invalid range', { from, to, docSize });
        }
      }
    } catch (error) {
      logger.debug('[DocumentStreaming] Highlight removal skipped due to error:', error);
    }
    
    highlightTimers.delete(timer);
  }, HIGHLIGHT_DURATION);
  
  highlightTimers.add(timer);
}

interface StreamingState {
  isStreaming: boolean;
  currentOperation: DocumentOperation | null;
  queuedOperations: DocumentOperation[];
}

interface UseDocumentStreamingProps {
  editor: Editor | null;
  enabled: boolean;
  onOperationApplied?: (operation: DocumentOperation) => void;
  onStreamComplete?: () => void;
  onDocumentSync?: (document: AeroDocument, s3VersionId?: string, preAIVersionId?: string) => void;
  setSuppressAutoSave?: (suppress: boolean) => void;
}

export function useDocumentStreaming({
  editor,
  enabled,
  onOperationApplied: _onOperationApplied,
  onStreamComplete,
  onDocumentSync,
  setSuppressAutoSave
}: UseDocumentStreamingProps) {
  const streamingStateRef = useRef<StreamingState>({
    isStreaming: false,
    currentOperation: null,
    queuedOperations: []
  });
  
  const applyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // ===== Server-Side Section Handlers =====
  
  /**
   * Handle section update from server
   * The server has already applied the operation to the JSON document,
   * we just need to update the TipTap view to match.
   * 
   * @param changedBlockIds - Optional array of specific block IDs that changed (for targeted highlighting)
   */
  const handleSectionUpdate = useCallback((
    sectionId: string, 
    section: DocumentSection, 
    operationType: string,
    changedBlockIds?: string[]
  ) => {
    if (!editor || !enabled) {
      logger.warn('[DocumentStreaming] Cannot handle section update: editor not ready');
      return;
    }
    
    logger.debug('[DocumentStreaming] handleSectionUpdate:', sectionId, operationType, 'changedBlocks:', changedBlockIds);
    
    // CRITICAL: Suppress auto-save during streaming updates
    // The server has already saved the document - we're just updating the view
    if (setSuppressAutoSave) {
      logger.debug('[DocumentStreaming] Suppressing auto-save during section update');
      setSuppressAutoSave(true);
    }
    
    try {
      // Find the existing section in TipTap by its ID
      let sectionStart: number | null = null;
      let sectionEnd: number | null = null;
      let sectionLevel: number | null = null;
      
      editor.state.doc.descendants((node, pos) => {
        if (node.type.name === 'heading' && node.attrs.id === sectionId) {
          sectionStart = pos;
          sectionLevel = node.attrs.level || 2;
          return false;
        }
      });
      
      // Find section end - IMPORTANT: Stop at ANY heading (including subsections)
      // This ensures we only replace the section's direct content, not its children
      // The server sends the section data without subsections, so we must preserve them
      if (sectionStart !== null && sectionLevel !== null) {
        sectionEnd = editor.state.doc.content.size;
        let foundEnd = false;
        
        editor.state.doc.descendants((node, pos) => {
          if (foundEnd) return false;
          if (pos <= sectionStart!) return;
          
          if (node.type.name === 'heading') {
            // Stop at ANY heading - this preserves subsections
            sectionEnd = pos;
            foundEnd = true;
            return false;
          }
        });
      }
      
      // Convert section to TipTap nodes with targeted highlighting
      const newNodes = sectionToTipTapNodes(section, { 
        includeHighlight: true, 
        operationId: sectionId,
        changedBlockIds: changedBlockIds  // Only highlight specific changed blocks
      });
      
      if (sectionStart !== null && sectionEnd !== null) {
        // Replace existing section
        logger.debug('[DocumentStreaming] Replacing section from', sectionStart, 'to', sectionEnd);
        
        editor.chain()
          .focus()
          .deleteRange({ from: sectionStart, to: sectionEnd })
          .insertContentAt(sectionStart, newNodes)
          .run();
      } else {
        // Section doesn't exist yet - find insertion point
        logger.debug('[DocumentStreaming] Section not found, inserting new section');
        
        let insertPos = editor.state.doc.content.size;
        const idParts = sectionId.split('-');
        
        if (idParts.length > 2) {
          // This is a subsection, find the parent section end
          const parentId = idParts.slice(0, -1).join('-');
          let parentPos: number | null = null;
          let parentLevel: number | null = null;
          
          editor.state.doc.descendants((node, pos) => {
            if (node.type.name === 'heading' && node.attrs.id === parentId) {
              parentPos = pos;
              parentLevel = node.attrs.level || 2;
              return false;
            }
          });
          
          if (parentPos !== null && parentLevel !== null) {
            let foundEnd = false;
            insertPos = editor.state.doc.content.size;
            
            editor.state.doc.descendants((node, pos) => {
              if (foundEnd) return false;
              if (pos <= parentPos!) return;
              
              if (node.type.name === 'heading') {
                const headingLevel = node.attrs.level || 2;
                if (headingLevel <= parentLevel!) {
                  insertPos = pos;
                  foundEnd = true;
                  return false;
                }
              }
            });
          }
        }
        
        editor.chain()
          .focus()
          .insertContentAt(insertPos, newNodes)
          .run();
      }
      
      // Scroll to the edited section after DOM updates
      scrollToSectionById(sectionId);
      
      // Schedule highlight removal for changed blocks (or entire section if no specific blocks)
      if (changedBlockIds && changedBlockIds.length > 0) {
        scheduleHighlightRemovalForBlocks(editor, changedBlockIds);
      } else {
        scheduleHighlightRemovalForSection(editor, sectionId);
      }
      
      // NOTE: Auto-save stays suppressed until document_sync is received.
      // Do NOT re-enable here - the 600ms gap between section_updates allowed
      // new auto-save timers to start mid-stream. handleDocumentSync re-enables it.
      
      // Notify DocumentViewerContent to clear any pre-existing auto-save timer
      // and prevent new timers from starting during streaming
      if (typeof window.__setDocumentAIStreaming === 'function') {
        window.__setDocumentAIStreaming(true);
      }
      
    } catch (error) {
      logger.error('[DocumentStreaming] Error handling section update:', error);
      if (setSuppressAutoSave) {
        setSuppressAutoSave(false);
      }
    }
  }, [editor, enabled, setSuppressAutoSave]);
  
  /**
   * Handle section deletion from server
   */
  const handleSectionDelete = useCallback((sectionId: string) => {
    if (!editor || !enabled) {
      logger.warn('[DocumentStreaming] Cannot handle section delete: editor not ready');
      return;
    }
    
    logger.debug('[DocumentStreaming] handleSectionDelete:', sectionId);
    
    try {
      let sectionStart: number | null = null;
      let sectionEnd: number | null = null;
      let sectionLevel: number | null = null;
      
      editor.state.doc.descendants((node, pos) => {
        if (node.type.name === 'heading' && node.attrs.id === sectionId) {
          sectionStart = pos;
          sectionLevel = node.attrs.level || 2;
          return false;
        }
      });
      
      if (sectionStart === null || sectionLevel === null) {
        logger.warn('[DocumentStreaming] Section not found for deletion:', sectionId);
        return;
      }
      
      sectionEnd = editor.state.doc.content.size;
      let foundEnd = false;
      
      editor.state.doc.descendants((node, pos) => {
        if (foundEnd) return false;
        if (pos <= sectionStart!) return;
        
        if (node.type.name === 'heading') {
          const headingLevel = node.attrs.level || 2;
          if (headingLevel <= sectionLevel!) {
            sectionEnd = pos;
            foundEnd = true;
            return false;
          }
        }
      });
      
      logger.debug('[DocumentStreaming] Deleting section from', sectionStart, 'to', sectionEnd);
      
      editor.chain()
        .focus()
        .deleteRange({ from: sectionStart, to: sectionEnd! })
        .run();
        
    } catch (error) {
      logger.error('[DocumentStreaming] Error handling section delete:', error);
    }
  }, [editor, enabled]);
  
  /**
   * Handle full document sync from server
   * @param document - The updated document
   * @param version - The document version timestamp
   * @param s3VersionId - The new S3 version ID after AI edits
   * @param preAIVersionId - The S3 version ID before AI edits (for undo)
   */
  const handleDocumentSync = useCallback((
    document: AeroDocument, 
    version: string,
    s3VersionId?: string,
    preAIVersionId?: string
  ) => {
    logger.debug('[DocumentStreaming] handleDocumentSync, version:', version, 
      's3VersionId:', s3VersionId, 'preAIVersionId:', preAIVersionId);
    
    // Re-enable auto-save now that the full document is synced
    // This is the ONLY place auto-save is re-enabled after streaming
    if (setSuppressAutoSave) {
      logger.debug('[DocumentStreaming] Re-enabling auto-save after document_sync');
      setSuppressAutoSave(false);
    }
    
    // Notify DocumentViewerContent that AI streaming is complete
    // so it can accept new auto-save timers from user edits
    if (typeof window.__setDocumentAIStreaming === 'function') {
      window.__setDocumentAIStreaming(false);
    }
    
    // Notify parent component about the document sync with version IDs
    if (onDocumentSync) {
      onDocumentSync(document, s3VersionId, preAIVersionId);
    }
  }, [onDocumentSync, setSuppressAutoSave]);
  
  // ===== Legacy Stubs (for backward compatibility) =====
  
  /**
   * Queue operations for streaming application
   * 
   * @deprecated Server-side operations via section_update events are the only supported path.
   * This stub exists only for backward compatibility and logs an error if called.
   */
  const queueOperations = useCallback((operations: DocumentOperation[]) => {
    logger.error('[DocumentStreaming] ❌ DEPRECATED: Client-side operations are no longer supported.');
    logger.error('[DocumentStreaming] Operations should be applied server-side via section_update events.');
    logger.error('[DocumentStreaming] Ensure jsonDocument is passed to AI context for server-side processing.');
    logger.error('[DocumentStreaming] Received', operations.length, 'operations that will NOT be applied:', 
      operations.map(op => op.op).join(', '));
    
    if (onStreamComplete) {
      onStreamComplete();
    }
  }, [onStreamComplete]);

  /**
   * Cancel streaming and clear queue
   */
  const cancelStreaming = useCallback(() => {
    streamingStateRef.current.queuedOperations = [];
    streamingStateRef.current.isStreaming = false;
    streamingStateRef.current.currentOperation = null;
    
    if (applyTimeoutRef.current) {
      clearTimeout(applyTimeoutRef.current);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    const applyTimeout = applyTimeoutRef.current;
    return () => {
      if (applyTimeout) {
        clearTimeout(applyTimeout);
      }
      // Clear all highlight timers
      highlightTimers.forEach(timer => clearTimeout(timer));
      highlightTimers.clear();
    };
  }, []);

  return {
    // Legacy stubs (deprecated)
    queueOperations,
    cancelStreaming,
    isStreaming: streamingStateRef.current.isStreaming,
    currentOperation: streamingStateRef.current.currentOperation,
    // Server-side section handlers (active)
    handleSectionUpdate,
    handleSectionDelete,
    handleDocumentSync
  };
}
